import { COMPETITION_WIN_CONDITIONS, PERMISSIONS_NAME, THEMES } from "lib/constants";
import moment from "moment-timezone";

export const removeArrayDuplications = (arr) =>
  arr.filter((item, index) => arr.indexOf(item) === index);

export const getFiscalQuarterIndex = (date, fiscalYearStart) => {
  fiscalYearStart = fiscalYearStart === undefined ? moment("0101", "MMDD") : fiscalYearStart;
  const fiscalStartMonth = fiscalYearStart.month() + 1;
  const month = date.month() + 1;

  // Account for mid-month fiscal year scenario
  if (month === fiscalStartMonth && date.startOf("day") < fiscalYearStart.startOf("day")) return 4;

  const monthIndex = (month - fiscalStartMonth + 12) % 12;
  return Math.floor(monthIndex / 3 + 1);
};

export const getFilterDates = (date, timezone = moment.tz.guess(), fiscalYearStart = "0101") => {
  let startDate;
  let endDate;
  if (date === "Today") {
    startDate = moment.tz(undefined, timezone).startOf("day").format("YYYY-MM-DD HH:mm:ss");
    endDate = moment.tz(undefined, timezone).endOf("day").format("YYYY-MM-DD HH:mm:ss");
  } else if (date === "This Week") {
    startDate = moment.tz(undefined, timezone).startOf("week").format("YYYY-MM-DD HH:mm:ss");
    endDate = moment.tz(undefined, timezone).endOf("week").format("YYYY-MM-DD HH:mm:ss");
  } else if (date === "Last Week") {
    startDate = moment
      .tz(undefined, timezone)
      .subtract(1, "weeks")
      .startOf("week")
      .format("YYYY-MM-DD HH:mm:ss");
    endDate = moment
      .tz(undefined, timezone)
      .subtract(1, "weeks")
      .endOf("week")
      .format("YYYY-MM-DD HH:mm:ss");
  } else if (date === "This Month") {
    startDate = moment.tz(undefined, timezone).startOf("month").format("YYYY-MM-DD HH:mm:ss");
    endDate = moment.tz(undefined, timezone).endOf("month").format("YYYY-MM-DD HH:mm:ss");
  } else if (date === "Last Month") {
    startDate = moment
      .tz(undefined, timezone)
      .subtract(1, "months")
      .startOf("month")
      .format("YYYY-MM-DD HH:mm:ss");
    endDate = moment
      .tz(undefined, timezone)
      .subtract(1, "months")
      .endOf("month")
      .format("YYYY-MM-DD HH:mm:ss");
  } else if (date === "This Quarter") {
    let fiscalStart = moment(fiscalYearStart, "MMDD");
    const quarterIndex = getFiscalQuarterIndex(moment(), fiscalStart);
    if (moment() < fiscalStart) fiscalStart = fiscalStart.subtract(1, "Y");
    startDate = moment
      .tz(fiscalStart, timezone)
      .add((quarterIndex - 1) * 3, "M")
      .format("YYYY-MM-DD HH:mm:ss");
    endDate = moment
      .tz(fiscalStart, timezone)
      .add(quarterIndex * 3, "M")
      .subtract(1, "ms")
      .format("YYYY-MM-DD HH:mm:ss");
  } else if (date === "Last Quarter") {
    let fiscalStart = moment(fiscalYearStart, "MMDD");
    const quarterIndex = getFiscalQuarterIndex(moment(), fiscalStart);
    if (moment() < fiscalStart) fiscalStart = fiscalStart.subtract(1, "Y");
    startDate = moment
      .tz(fiscalStart, timezone)
      .add((quarterIndex - 2) * 3, "M")
      .format("YYYY-MM-DD HH:mm:ss");
    endDate = moment
      .tz(fiscalStart, timezone)
      .add((quarterIndex - 1) * 3, "M")
      .subtract(1, "ms")
      .format("YYYY-MM-DD HH:mm:ss");
  } else if (date === "This Year") {
    let fiscalStart = moment(fiscalYearStart, "MMDD");
    if (moment() < fiscalStart) fiscalStart = fiscalStart.subtract(1, "Y");
    startDate = moment.tz(fiscalStart, timezone).format("YYYY-MM-DD HH:mm:ss");
    endDate = moment
      .tz(fiscalStart, timezone)
      .add(1, "Y")
      .subtract(1, "ms")
      .format("YYYY-MM-DD HH:mm:ss");
  } else if (date === "Last Year") {
    let fiscalStart = moment(fiscalYearStart, "MMDD").subtract(1, "Y");
    if (moment() < moment(fiscalYearStart, "MMDD")) fiscalStart = fiscalStart.subtract(1, "Y");
    startDate = moment.tz(fiscalStart, timezone).format("YYYY-MM-DD HH:mm:ss");
    endDate = moment
      .tz(fiscalStart, timezone)
      .add(1, "Y")
      .subtract(1, "ms")
      .format("YYYY-MM-DD HH:mm:ss");
  } else if (date === "Last 30 days") {
    startDate = moment
      .tz(undefined, timezone)
      .subtract(29, "days")
      .startOf("day")
      .format("YYYY-MM-DD HH:mm:ss");
    endDate = moment.tz(undefined, timezone).endOf("day").format("YYYY-MM-DD HH:mm:ss");
  } else if (date === "Last 60 days") {
    startDate = moment
      .tz(undefined, timezone)
      .subtract(59, "days")
      .startOf("day")
      .format("YYYY-MM-DD HH:mm:ss");
    endDate = moment.tz(undefined, timezone).endOf("day").format("YYYY-MM-DD HH:mm:ss");
  }
  return { startDate, endDate, timezone };
};

export const getRevenueDateRange = async (date, quotaPeriod, fiscalYearStart) => {
  let fiscalStart = moment(fiscalYearStart || "0101", "MMDD");
  let start = moment().startOf("day");
  let end = moment().endOf("day");

  switch (quotaPeriod.toLowerCase()) {
    case "weekly": {
      start = moment.utc(date.startOf("week")).format("YYYY-MM-DD HH:mm:ss");
      end = moment.utc(date.endOf("week")).format("YYYY-MM-DD HH:mm:ss");
      break;
    }
    case "quarterly": {
      const quarterIndex = await getFiscalQuarterIndex(date, fiscalStart);
      if (date < fiscalStart) fiscalStart = fiscalStart.subtract(1, "Y");
      start = moment(fiscalStart)
        .add((quarterIndex - 1) * 3, "M")
        .format("YYYY-MM-DD HH:mm:ss");
      end = moment(fiscalStart)
        .add(quarterIndex * 3, "M")
        .subtract(1, "ms")
        .format("YYYY-MM-DD HH:mm:ss");
      break;
    }
    case "monthly": {
      start = moment.utc(date.startOf("month")).format("YYYY-MM-DD HH:mm:ss");
      end = moment.utc(date.endOf("month")).format("YYYY-MM-DD HH:mm:ss");
      break;
    }
    case "annual":
    default:
      start = moment(fiscalStart).format("YYYY-MM-DD HH:mm:ss");
      end = moment(fiscalStart).add(1, "Y").subtract(1, "ms").format("YYYY-MM-DD HH:mm:ss");
      break;
  }

  return { start, end };
};

export const fetchDateRangeForWidget = (timePeriod, fiscalYearStart = "0101") => {
  let start;
  let end;
  if (timePeriod === "daily") {
    start = moment().utc().startOf("day").format("YYYY-MM-DD");
    end = moment().utc().endOf("day").format("YYYY-MM-DD");
  } else if (timePeriod === "weekly") {
    start = moment().utc().startOf("week").format("YYYY-MM-DD");
    end = moment().utc().endOf("week").format("YYYY-MM-DD");
  } else if (timePeriod === "monthly") {
    start = moment().utc().startOf("month").format("YYYY-MM-DD");
    end = moment().utc().endOf("month").format("YYYY-MM-DD");
  } else if (timePeriod === "quarterly") {
    let fiscalStart = moment(fiscalYearStart, "MMDD");
    const date = moment().utc();
    const quarterIndex = getFiscalQuarterIndex(date, fiscalStart);
    if (date < fiscalStart) fiscalStart = fiscalStart.subtract(1, "Y");
    start = moment(fiscalStart)
      .add((quarterIndex - 1) * 3, "months")
      .format("YYYY-MM-DD");
    end = moment(fiscalStart)
      .add(quarterIndex * 3, "months")
      .subtract(1, "milliseconds")
      .format("YYYY-MM-DD");
  } else if (timePeriod === "yearly") {
    start = moment().utc().startOf("year").format("YYYY-MM-DD");
    end = moment().utc().endOf("year").format("YYYY-MM-DD");
  }
  return { start, end };
};

export const formatDatesForWidget = (timePeriod, periodDates) => {
  let display = "";
  if (timePeriod === "daily") {
    display = moment.utc(periodDates.start).format("MMM D");
  } else if (timePeriod === "weekly") {
    const format = "MMM D";
    // const format = "M/D";
    const start = moment.utc(periodDates.start).format(format);
    const end = moment.utc(periodDates.end).format(format);
    display = `${start} - ${end}`;
  } else if (timePeriod === "monthly") {
    display = moment.utc(periodDates.start).format("MMM");
  } else if (timePeriod === "quarterly") {
    const format = "MMM D";
    const start = moment.utc(periodDates.start).format(format);
    const end = moment.utc(periodDates.end).format(format);
    return `${start} - ${end}`;
  } else if (timePeriod === "yearly") {
    display = moment.utc(periodDates.start).format("YYYY");
  }
  return display;
};

export const handlePeriodsForWidget = (type, timePeriod, periodDates) => {
  if (!periodDates.start && !periodDates.end) return {};

  let start;
  let end;
  switch (type) {
    case "prev":
      if (timePeriod === "daily") {
        start = moment.utc(periodDates.start).subtract(1, "days").format("YYYY-MM-DD");
        end = moment.utc(periodDates.end).subtract(1, "days").endOf("day").format("YYYY-MM-DD");
      } else if (timePeriod === "weekly") {
        start = moment.utc(periodDates.start).subtract(1, "weeks").format("YYYY-MM-DD");
        end = moment.utc(periodDates.end).subtract(1, "weeks").endOf("day").format("YYYY-MM-DD");
      } else if (timePeriod === "monthly") {
        start = moment.utc(periodDates.start).subtract(1, "months").format("YYYY-MM-DD");
        end = moment.utc(periodDates.end).subtract(1, "months").endOf("month").format("YYYY-MM-DD");
      } else if (timePeriod === "quarterly") {
        start = moment.utc(periodDates.start).subtract(3, "months").format("YYYY-MM-DD");
        end = moment.utc(periodDates.end).subtract(3, "months").endOf("month").format("YYYY-MM-DD");
      } else if (timePeriod === "yearly") {
        start = moment
          .utc(periodDates.start)
          .subtract(1, "years")
          .startOf("year")
          .format("YYYY-MM-DD");
        end = moment.utc(periodDates.end).subtract(1, "years").endOf("year").format("YYYY-MM-DD");
      }
      break;
    case "next":
      if (timePeriod === "daily") {
        start = moment.utc(periodDates.start).add(1, "days").format("YYYY-MM-DD");
        end = moment.utc(periodDates.end).add(1, "days").endOf("day").format("YYYY-MM-DD");
      } else if (timePeriod === "weekly") {
        start = moment.utc(periodDates.start).add(1, "weeks").format("YYYY-MM-DD");
        end = moment.utc(periodDates.end).add(1, "weeks").endOf("day").format("YYYY-MM-DD");
      } else if (timePeriod === "monthly") {
        start = moment.utc(periodDates.start).add(1, "months").format("YYYY-MM-DD");
        end = moment.utc(periodDates.end).add(1, "months").endOf("month").format("YYYY-MM-DD");
      } else if (timePeriod === "quarterly") {
        start = moment.utc(periodDates.start).add(3, "months").format("YYYY-MM-DD");
        end = moment.utc(periodDates.end).add(3, "months").endOf("month").format("YYYY-MM-DD");
      } else if (timePeriod === "yearly") {
        start = moment.utc(periodDates.start).add(1, "years").startOf("year").format("YYYY-MM-DD");
        end = moment.utc(periodDates.end).add(1, "years").endOf("year").format("YYYY-MM-DD");
      }
      // Don't allow dates beyond current period
      if (moment.utc(start).isSameOrAfter(moment.utc())) {
        start = periodDates.start;
        end = periodDates.end;
      }
      break;
    default:
      break;
  }
  return { start, end };
};

export const getPointsTextColor = (points, total = null, disabled = false) => {
  if (disabled) return THEMES.PRIMARY.GRAY[40];
  if (points < 0 || Number.isNaN(Number(points)) || points === null) {
    return "";
  }

  if (points <= (total ? total * 0.7 : 70)) {
    return THEMES.PRIMARY.RED_SECONDARY;
  }
  return THEMES.PRIMARY.GREEN;
};

export const removeYearFromDateFormat = (dateFormat) => {
  if (!dateFormat) return "MM/DD";
  let str = dateFormat.replaceAll("Y", "");
  str = str.replaceAll("y", "");

  if (str[0] === "-") {
    str = str.slice(1);
  }
  if (str[str.length - 1] === "-") {
    str = str.slice(0, str.length - 1);
  }
  return str;
};

export const checkPermission = (permission, userPermission) =>
  (permission !== PERMISSIONS_NAME.INPUT_KPIS && userPermission?.[PERMISSIONS_NAME.ADMIN]) ||
  userPermission?.[permission];

export const currencyFormat = (amount) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);

export const numberFormatter = (number, decimalNumber = false) => {
  const formattedNumber = new Intl.NumberFormat().format(number);

  if (decimalNumber) return `${formattedNumber}${formattedNumber % 1 === 0 ? ".0" : ""}`;
  return formattedNumber;
};

export const calculateKpiPoints = (count, points, currentValue, maxPointsPerDay) => {
  if (count === 0 || points === 0) return 0;
  // below line helps us avoid intermediate values as achieved points
  currentValue = parseInt(currentValue / count, 10) * count;
  const preMaxPointsAppliedPoints = parseInt((currentValue * points) / count, 10);
  return maxPointsPerDay
    ? Math.min(maxPointsPerDay, preMaxPointsAppliedPoints)
    : preMaxPointsAppliedPoints;
};

export const getWinConditionDescription = (winCon, endTime = null) => {
  if (!winCon) return "-";
  if (winCon === COMPETITION_WIN_CONDITIONS.FIRST_TO_KPI_TARGET && endTime)
    return "First user who achieves the KPI target or user with the highest KPI actions before the competition ends wins.";
  switch (winCon) {
    case COMPETITION_WIN_CONDITIONS.TOTAL_KPIS:
      return "User who achieves the most KPI actions before competition ends wins.";

    case COMPETITION_WIN_CONDITIONS.TOTAL_POINTS:
      return "User with the highest KPI actions points before competition ends wins.";

    case COMPETITION_WIN_CONDITIONS.FIRST_TO_KPI_TARGET:
      return "First user who achieves the KPI target points wins.";

    default:
      return "-";
  }
};

export const formatTimeRemaining = ({ days, hours, minutes, seconds, months }) => {
  const timeUnits = [];

  if (months >= 1) {
    timeUnits.push(`${months} Month${months === 1 ? "" : "s"}`);
    timeUnits.push(`${days} Day${days === 1 ? "" : "s"}`);
  } else if (days > 0) {
    timeUnits.push(`${days} Day${days === 1 ? "" : "s"}`);
    timeUnits.push(`${hours}h`);
    timeUnits.push(`${minutes}m`);
  } else if (hours > 0) {
    timeUnits.push(`${hours}h`);
    timeUnits.push(`${minutes}m`);
    timeUnits.push(`${seconds}s`);
  } else if (minutes > 0) {
    timeUnits.push(`${minutes}m`);
    timeUnits.push(`${seconds}s`);
  } else if (seconds > 0) {
    timeUnits.push(`${seconds}s`);
  }

  const formattedTime = timeUnits.join(", ");
  const lastCommaIndex = formattedTime.lastIndexOf(",");
  if (lastCommaIndex !== -1) {
    const beforeLastComma = formattedTime.slice(0, lastCommaIndex);
    const afterLastComma = formattedTime.slice(lastCommaIndex + 2);
    return `${beforeLastComma} and ${afterLastComma}`;
  }

  return formattedTime;
};

export const formatNumberWithComma = (number, decimal = true) => {
  const numberToString = typeof number === "number" ? String(number) : number;
  let result = numberToString;
  const x = numberToString.split(".");
  const y = x[0].replace(/,/gi, "");
  result = y.replace(/\d(?=(?:\d{3})+$)/g, "$&,");
  if (decimal && x.length > 1) result += `.${x[1]}`;
  return result;
};
